import { PALETTE } from "@eldrun/content";
import type { CharacterSurfaceStyle } from "../render/character-surface/config.ts";

/**
 * Ajustes da superfície dos atores pela URL do visor (F120): `superficie=0` desliga; `vidro` e
 * `fresnel` trocam os números (o resto fica o do papel; a costura segue o golpe a cada quadro);
 * `cor=<classe>|chefe|inimigo|neutro` troca a cor de todos. Valores inválidos ficam de fora (as faixas
 * são do `clampStyle`). Fase 15: `linhas`, `largura`, `densidade`, `brilho`, `raio` e `semente` saíram
 * com as linhas e os nós.
 */
export const SURFACE_PARAMS = {
  vidro: "glass",
  fresnel: "fresnel",
} as const satisfies Record<string, keyof CharacterSurfaceStyle>;

/** A cor de um nome da URL: classe, papel ou neutro; `undefined` se não conhece. */
export function surfaceColor(name: string | null): string | undefined {
  if (name === null) return undefined;
  const classes = PALETTE.classes as Readonly<Record<string, { hex: string } | undefined>>;
  if (classes[name]) return classes[name].hex;
  if (name === "chefe" || name === "inimigo" || name === "neutro") return PALETTE[name].hex;
  return undefined;
}

export function surfaceFromParams(params: URLSearchParams): Partial<CharacterSurfaceStyle> {
  const out: Partial<CharacterSurfaceStyle> = {};
  if (params.get("superficie") === "0") out.enabled = false;
  for (const [param, field] of Object.entries(SURFACE_PARAMS)) {
    const raw = params.get(param);
    const value = raw === null || raw.trim() === "" ? NaN : Number(raw);
    if (Number.isFinite(value)) (out as Record<string, number>)[field] = value;
  }
  const color = surfaceColor(params.get("cor"));
  if (color) out.accentColor = color;
  return out;
}

/** O parâmetro da URL de um campo numérico (o inverso de `SURFACE_PARAMS`). */
const PARAM_OF = Object.fromEntries(Object.entries(SURFACE_PARAMS).map(([param, field]) => [field, param])) as Readonly<Record<string, string>>;

/**
 * Escreve um ajuste do painel na URL (F120: a URL é a fonte de verdade do visor; o "Aplicar" recarrega
 * com tudo): `enabled` vira `superficie=0` (ou some), `cor` é o nome (`colorName`), os números pelo
 * nome curto. Campos sem parâmetro ficam de fora.
 */
export function writeSurfaceParams(params: URLSearchParams, change: Partial<CharacterSurfaceStyle>, colorName?: string): void {
  for (const [field, value] of Object.entries(change)) {
    if (field === "enabled") {
      if (value === false) params.set("superficie", "0");
      else params.delete("superficie");
    } else if (field === "accentColor") {
      if (colorName && surfaceColor(colorName) === value) params.set("cor", colorName);
    } else if (PARAM_OF[field] && typeof value === "number" && Number.isFinite(value)) {
      params.set(PARAM_OF[field], String(value));
    }
  }
}
